
import { useState, useEffect } from "react";
import DashboardLayout from "@/layouts/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";

const Dashboard = () => {
  // Debug log to track component rendering
  useEffect(() => {
    console.log("[Dashboard] Component rendered");
    return () => console.log("[Dashboard] Component unmounting");
  }, []);

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error("User not authenticated");
        }
        
        console.log("[Dashboard] Fetching stats for user:", user.id);
        
        // Fetch courses count
        const { data: courses, error: coursesError } = await supabase
          .from('courses')
          .select('id')
          .eq('creator_id', user.id);
        
        if (coursesError) throw coursesError;
        
        // Get course IDs for this creator
        const courseIds = courses?.map(course => course.id) || [];
        
        // If no courses, return empty stats
        if (courseIds.length === 0) {
          return {
            totalCourses: 0,
            totalModules: 0,
            totalLessons: 0
          };
        }
        
        // Fetch modules count for this creator's courses
        const { data: modules, error: modulesError } = await supabase
          .from('modules')
          .select('id')
          .in('course_id', courseIds);
        
        if (modulesError) throw modulesError;
        
        // Get module IDs for this creator's courses
        const moduleIds = modules?.map(module => module.id) || [];
        
        // If no modules, return stats with zero modules and lessons
        if (moduleIds.length === 0) {
          return {
            totalCourses: courseIds.length,
            totalModules: 0,
            totalLessons: 0
          };
        }
        
        // Fetch lessons count for this creator's modules
        const { data: lessons, error: lessonsError } = await supabase
          .from('lessons')
          .select('id')
          .in('module_id', moduleIds);
        
        if (lessonsError) throw lessonsError;
        
        console.log("[Dashboard] Stats fetched successfully:", {
          courses: courseIds.length,
          modules: moduleIds.length,
          lessons: lessons?.length || 0
        });
        
        return {
          totalCourses: courseIds.length,
          totalModules: moduleIds.length,
          totalLessons: lessons?.length || 0
        };
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        throw error;
      }
    }
  });

  if (isLoading) {
    return (
      <DashboardLayout 
        title="Dashboard" 
        description="Welcome back! Here's an overview of your course platform."
      >
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    toast.error("Could not load dashboard data");
    console.error("Dashboard data error:", error);
  }

  return (
    <DashboardLayout 
      title="Dashboard" 
      description="Welcome back! Here's an overview of your course platform."
    >
      {/* Stats Section */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Courses</CardDescription>
            <CardTitle className="text-3xl">{stats?.totalCourses || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground">
              <Link to="/dashboard/courses" className="text-primary hover:underline">
                View all courses
              </Link>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Modules</CardDescription>
            <CardTitle className="text-3xl">{stats?.totalModules || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground">
              Across all your courses
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Lessons</CardDescription>
            <CardTitle className="text-3xl">{stats?.totalLessons || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground">
              Across all your modules
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Quick Actions */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks to help you get started</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <Link to="/dashboard/courses/new">
              <Button variant="outline" className="w-full h-auto flex-col items-center justify-center py-6 gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" x2="12" y1="5" y2="19"></line>
                  <line x1="5" x2="19" y1="12" y2="12"></line>
                </svg>
                <span>Create Course</span>
              </Button>
            </Link>
            
            <Link to="/dashboard/profile">
              <Button variant="outline" className="w-full h-auto flex-col items-center justify-center py-6 gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"></path>
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
                </svg>
                <span>Edit Profile</span>
              </Button>
            </Link>
            
            <Link to="/dashboard/courses">
              <Button variant="outline" className="w-full h-auto flex-col items-center justify-center py-6 gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
                <span>Manage Courses</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
      
      {stats && stats.totalCourses === 0 ? (
        <Card>
          <CardContent className="p-6 flex flex-col items-center justify-center text-center py-10">
            <div className="rounded-full bg-primary/10 p-6 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">Get Started with Your First Course</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Ready to share your knowledge? Create your first course and start building your online education platform.
            </p>
            <Link to="/dashboard/courses/new">
              <Button size="lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                  <line x1="12" x2="12" y1="5" y2="19"></line>
                  <line x1="5" x2="19" y1="12" y2="12"></line>
                </svg>
                Create Your First Course
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Latest Activity</CardTitle>
              <CardDescription>Your recent course updates</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-center py-8">
              Activity tracking will be available in a future update.
            </p>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
